import {Injectable} from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import type { DamageReportModel } from 'src/packages/damage-reporting';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import {MachineModel} from 'src/app/models/machine';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import {OrderModel} from 'src/app/models/order';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import {
  GalleryAdapter
} from 'src/app/pages/processing/orders/order-details/writeable-checklist-details/writeable-checklist-module/GalleryAdapter';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import {TemporaryImageService} from 'src/app/providers/model-services/writeable-checklist/temporary-image.service';
import type { IChecklist } from 'src/packages/checklistManagement';
import {IHeaderAndFooterModel} from 'src/packages/templateManagement/interfaces';
import {ITemplate} from 'src/packages/templateManagement/models/template';
import {ITemplateColumn} from 'src/packages/templateManagement/models/tp-col';
import {ITemplateRow} from 'src/packages/templateManagement/models/tp-row';
import {IMitsPDF, IMitsPDFInfo, IMitsPDFOptions,} from '../../../interfaces/mits-pdf';
import {ChecklistGenerator} from './includes/checklistGenerator';
import {ImageGenerator} from './includes/imageGenerator';
import {ModuleGenerator} from './includes/moduleGenerator';
import {MitsPDFAssets} from './mits-pdf-assets';
import {MitsPDFFormatter} from './mits-pdf-formatter';

export type IMitsPDFAcceptedObjectTypes =
  | OrderModel
  | IChecklist
  | DamageReportModel;

@Injectable({
  providedIn: 'root',
})
export class MitsPDFGenerator {
  docDef!: IMitsPDF;
  template!: ITemplate;
  object!: IMitsPDFAcceptedObjectTypes;
  mandator!: IHeaderAndFooterModel;
  business!: IHeaderAndFooterModel;

  /** CONSTANTS */
  private readonly pageWidth = 595;
  private readonly headerHeight = 150;
  private readonly footerHeight = 85;

  private imageGenerator!: ImageGenerator;
  private moduleGenerator!: ModuleGenerator;

  /**
   * Default-Constructor
   */
  constructor(private readonly temporaryImageService: TemporaryImageService) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
  }

  public openPDF(pdf: IMitsPDF) {
    const pdfDocGenerator = pdfMake.createPdf(pdf);
    pdfDocGenerator.open();
  }

  public async getDataUrlFromPDF(pdf: IMitsPDF): Promise<string> {
    const pdfDocGenerator = pdfMake.createPdf(pdf);
    return new Promise((resolve) => {
      pdfDocGenerator.getDataUrl((dataUrl: string) => {
        resolve(dataUrl);
      });
    });
  }

  public convertDataUrlToBlob(dataUrl: string): Blob {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }

  /**
   * Converts a template to a mitsPDF
   * @param template to convert from
   * @param object to use the data of
   * @param mandator to get the header or other data
   * @param business to get the header or other data
   * @param options to apply to the PDF
   * @returns converted template in type mitsPDF
   */
  public convertTemplateToMitsPDF(
    template: ITemplate,
    object: IMitsPDFAcceptedObjectTypes,
    mandator: IHeaderAndFooterModel,
    business: IHeaderAndFooterModel,
    options?: IMitsPDFOptions
  ): IMitsPDF {
    this.docDef = {} as IMitsPDF;
    this.template = template;
    this.object = object;
    this.mandator = mandator;
    this.business = business;
    this.initializeGenerators();
    this.generateBasicDefinition();
    this.generateInfo(this.template.name);
    this.generateHeader();
    this.generateFooter();
    this.generateContent();
    if (options && (options.signature_self || options.signature_third)) {
      this.generateSignatures(
        options.signature_self,
        options.signature_third,
        options.signature_self_name,
        options.signature_third_name
      );
    }
    return this.docDef;
  }

  /**
   * Converts a checklist to a mitsPDF
   * @param checklist to use the data of
   * @param order to use the data of
   * @param mandator to get the header or other data
   * @param business to get the header or other data
   * @param options to apply to the PDF
   * @param machine to use the data of
   * @returns converted template in type mitsPDF
   */
  public async convertChecklistToMitsPDF(
    checklist: IChecklist,
    order: OrderModel,
    mandator: IHeaderAndFooterModel,
    business: IHeaderAndFooterModel,
    options?: IMitsPDFOptions,
    machine?: MachineModel
  ): Promise<IMitsPDF> {
    this.docDef = {} as IMitsPDF;
    this.object = checklist;
    this.mandator = mandator;
    this.business = business;
    this.initializeGenerators();
    this.generateBasicDefinition();
    this.generateInfo(checklist.name);
    this.generateHeader();
    this.generateFooter();
    const galleryAdapter: GalleryAdapter = new GalleryAdapter(
      this.temporaryImageService
    );
    const checklistGenerator = new ChecklistGenerator(
      galleryAdapter,
      this.imageGenerator,
      this.docDef,
      checklist,
      order,
      machine
    );
    this.docDef.content = await checklistGenerator.getChecklistPDFContent();
    if (options && (options.signature_self || options.signature_third)) {
      this.generateSignatures(
        options.signature_self,
        options.signature_third,
        options.signature_self_name,
        options.signature_third_name
      );
    }
    return this.docDef;
  }

  /**
   * Verschiedene Generatoren für die Formatierung erzeugen
   */
  private initializeGenerators() {
    this.imageGenerator = new ImageGenerator(this.docDef);
    this.moduleGenerator = new ModuleGenerator(
      this.object,
      this.docDef,
      this.imageGenerator,
      new MitsPDFFormatter(),
      new MitsPDFAssets()
    );
  }

  private generateBasicDefinition(): void {
    this.docDef.info = {} as IMitsPDFInfo;
    this.docDef.header = {};
    this.docDef.footer = {};
    this.docDef.content = [];
    this.docDef.images = {};
    this.docDef.styles = {};
    this.docDef.pageSize = 'A4';
    this.docDef.pageOrientation = 'portrait';
    this.docDef.pageMargins = [
      this.template ? Number(this.template.element_style.margin.left) : 16,
      this.template ? Number(this.template.element_style.margin.top) : 0,
      this.template ? Number(this.template.element_style.margin.right) : 16,
      this.template ? Number(this.template.element_style.margin.bottom) : 0,
    ];
  }

  private getDocumentWidth(substractPageMargins: boolean): number {
    return substractPageMargins
      ? this.pageWidth -
          this.template.element_style.margin.left -
          this.template.element_style.margin.right
      : this.pageWidth;
  }

  private generateInfo(name?: string): void {
    let author = '';
    if (this.mandator.name1) {
      author += this.mandator.name1;
    }
    if (this.mandator.name2) {
      author += ' ' + this.mandator.name2;
    }
    if (this.mandator.name3) {
      author += ' ' + this.mandator.name3;
    }
    this.docDef.info = {
      title: name || '',
      author,
      subject: '',
      keywords: '',
    };
  }

  private generateHeader(): void {
    const busHeader = this.business ? this.business.header64 : undefined;
    const manHeader = this.mandator.header64;

    if (busHeader || manHeader) {
      const b64 = busHeader ? busHeader : manHeader;
      this.docDef.header.image = this.imageGenerator.attachImage(b64);
      this.docDef.pageMargins[1] += this.headerHeight;
      this.docDef.header.fit = [
        this.getDocumentWidth(false).toString(),
        this.headerHeight.toString(),
      ];
    }
  }

  private generateFooter(): void {
    const busFooter = this.business ? this.business.footer64 : undefined;
    const manFooter = this.mandator.footer64;

    if (busFooter || manFooter) {
      const b64 = busFooter ? busFooter : manFooter;
      this.docDef.footer.image = this.imageGenerator.attachImage(b64);
      this.docDef.pageMargins[3] += this.footerHeight;
      this.docDef.footer.fit = [
        this.getDocumentWidth(false).toString(),
        this.footerHeight.toString(),
      ];
    }
  }

  private generateContent(): void {
    this.docDef.content = this.docDef.content.concat(
      this.parseTemplateRows(this.template.rows)
    );
  }

  private generateSignatures(
    self?: string,
    third?: string,
    selfName?: string,
    thirdName?: string
  ): void {
    const emptyRect =
      'data:image/png;base64,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';
    const halfWidth = ((this.pageWidth - 32) / 2).toString();
    this.docDef.content.push({
      margin: [0, 40, 0, 0],
      columns: [
        {
          image: third ? this.imageGenerator.attachImage(third) : emptyRect,
          fit: [halfWidth, '150'],
        },
        {
          image: self ? this.imageGenerator.attachImage(self) : emptyRect,
          fit: [halfWidth, '150'],
        },
      ],
    });
    this.docDef.content.push({
      columns: [
        {
          text: thirdName ? thirdName : 'Auftraggeber',
          alignment: 'left',
          margin: 16,
        },
        {
          text: selfName ? selfName : 'Bearbeiter',
          alignment: 'left',
          margin: 16,
        },
      ],
    });
  }

  private parseTemplateRows(templateRows: ITemplateRow[]): Array<any> {
    const generatedRows: unknown[] = [];
    templateRows.forEach((row) => {
      if (row.template_for) {
        this.moduleGenerator.clearParentKeysTemplateFor(row);
        const rowObjects = (this.object as unknown as Record<string, unknown>)[
          row.template_for
        ] as Array<any>;
        const originalObject = this.moduleGenerator.object;

        this.moduleGenerator.addParentKey(row.template_for);

        // TODO Der Filter kann auch dynamisch gelöst werden
        rowObjects
          .filter((rowObject) => !rowObject.report_canceled)
          .forEach((rowObject, index) => {
            this.moduleGenerator.object = rowObject;
            const generatedRow = this.generateRow(row);
            // Jede Maschine (außer der ersten) beginnt auf einer neuen Seite,
            // damit die Maschinen-Berichte in PDF und Preview sauber getrennt sind.
            if (index > 0) {
              generatedRow.pageBreak = 'before';
            }
            generatedRows.push(generatedRow);
          });

        this.moduleGenerator.object = originalObject;
        this.moduleGenerator.popParentKey();
      } else {
        generatedRows.push(this.generateRow(row));
      }
    });
    return [generatedRows];
  }

  /**
   * Einzelne Zeile generieren
   */
  private generateRow(row: ITemplateRow): any {
    return {
      width: '100%',
      margin: [
        Number(row.element_style.margin.left),
        Number(row.element_style.margin.top),
        Number(row.element_style.margin.right),
        Number(row.element_style.margin.bottom),
      ],
      columns:
        row.cols && row.cols.length > 0 ? this.parseTemplateCols(row.cols) : [],
    };
  }

  private parseTemplateCols(templateCols: ITemplateColumn[]): Array<any> {
    const columns: unknown[] = [];
    templateCols.forEach((c) => {
      const newCol: any = {
        width: '*',
        margin: [
          Number(c.element_style.margin.left),
          Number(c.element_style.margin.top),
          Number(c.element_style.margin.right),
          Number(c.element_style.margin.bottom),
        ],
      };
      if (c.rows && c.rows.length > 0) {
        newCol.columns = this.parseTemplateRows(c.rows);
      } else if (c.module) {
        newCol.columns = [this.moduleGenerator.parseTemplateModule(c.module)];
      }
      columns.push(newCol);
    });
    return columns;
  }
}
